<template>
	<b-modal v-model="show" id="print-dispatch-summary" :title="title" size="xl" ref="print-dispatch-summary"
		:cancel-disabled="disableConfirmButtons" :ok-disabled="disableConfirmButtons" :no-close-on-backdrop="true">
		<loading :active.sync="isLoading" loader="spinner" color="#20A8D8" :is-full-page="false" />

		<b-container v-show="selDispatch !== null">
			<b-row class="px-3 pt-2 pb-1">
				<b-col sm="12" md="6">
					<b-form-group label="No. of Dockets" label-class="font-weight-bold pt-0" class="mb-2">
						<b-form-radio-group v-model="selectedCopyCount" :options="copyCountOptions" buttons
							button-variant="outline-primary" size="sm" />
					</b-form-group>
				</b-col>
			</b-row>

			<div class="page-container" id="dispatch-summary-container">
				<div v-for="(copyOwner, index) in selectedCopyOwners" :key="copyOwner" class="dispatch-summary-block">
					<div class="dispatch-summary">
					<!-- Header -->
					<DispatchSummaryHeader :selDispatch="selDispatch" />

					<!-- Source and Destination -->
					<DispatchSummarySourceAndDestination :selDispatch="selDispatch" :sourceLocation="sourceLocation"
						:destinationLocation="destinationLocation" />

					<!-- Assets -->
					<DispatchSummaryAssetTypes :selDispatch="selDispatch" />

					<!-- Remarks -->
					<b-row class="px-2 mb-2 remarks-section">
						<b-col sm="6" class="border border-light border-1 p-2">
							<span class="section-header">DISPATCH REMARKS:</span>&nbsp;
							<span class="remarks-value" v-html="dispatchRemarks"></span>
						</b-col>
						<b-col sm="6" class="border border-light border-1 p-2">
							<span class="section-header">RECEIPT REMARKS:</span>&nbsp;
							<span class="remarks-value" v-html="receiptRemarks"></span>
						</b-col>
					</b-row>

					<!-- Signatories -->
					<DispatchSummarySignatories :selDispatch="selDispatch" :deployedByUser="deployedByUser"
						:receivedByUser="receivedByUser" />

					<!-- Footer -->
					<DispatchSummaryFooter :copyOwner="copyOwner" />
				</div>

					<div v-if="index < selectedCopyOwners.length - 1" class="divider-container">
						<i class="fa fa-scissors divider-icon"></i>
						<hr class="divider-line" />
					</div>
				</div>
			</div>
		</b-container>

		<template #modal-footer>
			<div class="w-100">
				<span class="float-right mr-3">
					<b-button variant="secondary" @click="show = false" class="footer-button mr-2"
						:disabled="disableConfirmButtons">
						Close
					</b-button>
					<b-button v-show="hasDispatchNo" variant="primary" @click="printDispatch" class="footer-button"
						:disabled="disableConfirmButtons">
						Print
					</b-button>
				</span>
			</div>
		</template>
	</b-modal>
</template>

<script>
// Components
import DispatchSummaryHeader from './summary/DispatchSummaryHeader';
import DispatchSummarySourceAndDestination from './summary/DispatchSummarySourceAndDestination';
import DispatchSummaryAssetTypes from './summary/DispatchSummaryAssetTypes';
import DispatchSummarySignatories from './summary/DispatchSummarySignatories';
import DispatchSummaryFooter from './summary/DispatchSummaryFooter';

// Util
import { DispatchUtil } from '@/utils/dispatchUtil';

// Others
import EventBus from '@/shared/event-bus';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import _ from 'lodash';

export default {
	name: 'print-dispatch-summary',
	components: {
		DispatchSummaryHeader,
		DispatchSummarySourceAndDestination,
		DispatchSummaryAssetTypes,
		DispatchSummarySignatories,
		DispatchSummaryFooter,
		Loading,
	},
	props: {
		allStorageLocationsObj: {
			type: Object,
			required: true,
		},
		allUsersObj: {
			type: Object,
			required: true,
		}
	},
	data() {
		return {
			show: false,
			selDispatch: {},
			sourceLocation: {},
			destinationLocation: {},
			deployedByUser: '',
			receivedByUser: '',

			selectedCopyCount: 2,
			copyCountOptions: [
				{ text: '2 Dockets', value: 2 },
				{ text: '3 Dockets', value: 3 },
			],
			configTimeout: 5000,
			isLoading: false,
		};
	},
	computed: {
		title() {
			return 'View Dispatch';
		},
		disableConfirmButtons() {
			return this.isLoading;
		},
		dispatchRemarks() {
			let remarks = '-';
			let proofOfDispatch = this.selDispatch ? this.selDispatch.proofOfDispatch : null;

			if (proofOfDispatch) {
				remarks = proofOfDispatch.remarks && proofOfDispatch.remarks.length > 0 ? proofOfDispatch.remarks : '-';
			} else {
				let notes = this.selDispatch ? this.selDispatch.notes : null;
				remarks = notes && notes.length > 0 ? notes : '-';
			}

			return this.truncateRemarks(remarks);
		},
		receiptRemarks() {
			let remarks = '-';

			let proofOfReceipt = this.selDispatch.proofOfReceipt;
			if (proofOfReceipt) {
				remarks = proofOfReceipt.remarks && proofOfReceipt.remarks.length > 0 ? proofOfReceipt.remarks : '-';
			}

			return this.truncateRemarks(remarks);
		},
		dispatchNo() {
			let dispatch = this.selDispatch ? this.selDispatch : {};
			return dispatch.dispatchNo ? dispatch.dispatchNo : "-";
		},
		hasDispatchNo() {
			let dispatch = this.selDispatch ? this.selDispatch : {};
			return dispatch.dispatchNo && dispatch.dispatchNo.length > 0 ? true : false;
		},
		isDriverActive() {
			let driver = this.selDispatch && this.selDispatch.driver ? this.selDispatch.driver : {};
			return driver && driver.userId && driver.userId.length > 0 ? true : false;
		},
		selectedCopyOwners() {
			let copyOwners = ["Dispatcher's", "Driver's"];

			if (this.selectedCopyCount === 3) {
				copyOwners.push("Receiver's");
			}

			return copyOwners;
		}
	},
	mounted() {
		EventBus.$on('onPrintDispatch', (selDispatch) => {
			this.onReset();

			if (!_.isEmpty(selDispatch)) {
				this.selDispatch = DispatchUtil.cleanupFields(selDispatch);
				this.retrieveOtherDetails(selDispatch);
			}

			this.$bvModal.show('print-dispatch-summary');
		});
	},
	methods: {
		retrieveOtherDetails(selDispatch) {
			// Show loader
			this.isLoading = true;

			let sourceLocId = selDispatch.source.storageLocationId;
			this.sourceLocation = this.allStorageLocationsObj[sourceLocId];

			let destinationLocId = selDispatch.destination.storageLocationId;
			this.destinationLocation = this.allStorageLocationsObj[destinationLocId];

			if (this.isDriverActive || this.selDispatch.status === 'Draft') {
				this.deployedByUser = this.getNameDisplay(this.allUsersObj[selDispatch.createdBy]);
			} else {
				this.deployedByUser = this.getNameDisplay(this.allUsersObj[selDispatch.deployedBy]);
			}

			if (selDispatch.receivedBy && selDispatch.receivedBy.length > 0) {
				this.receivedByUser = this.getNameDisplay(this.allUsersObj[selDispatch.receivedBy]);
			}

			// Check for loader
			this.isLoading = false;
		},

		async printDispatch() {
			try {
				// Show loading indicator
				this.isLoading = true;

				await this.downloadSelectedCopies();

				// Hide modal and reset
				this.$bvModal.hide('print-dispatch-summary');
				this.onReset();
			} catch (error) {
				console.error('Error occurred:', error);
			} finally {
				// Hide loading indicator
				this.isLoading = false;
			}
		},

		async downloadSelectedCopies() {
			try {
				const container = document.getElementById('dispatch-summary-container');
				const filename = this.getFileName();
				const canvas = await html2canvas(container, { scale: 2 });
				const imgData = canvas.toDataURL('image/jpeg', 1.0);
				const pdf = new jsPDF({
					unit: 'mm',
					format: 'a4',
					orientation: 'portrait'
				});

				const pdfWidth = 210;
				const pdfHeight = 297;
				const imgWidth = pdfWidth;
				const imgHeight = (canvas.height * imgWidth) / canvas.width;
				let heightLeft = imgHeight;
				let position = 0;

				pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
				heightLeft -= pdfHeight;

				while (heightLeft > 0) {
					position = heightLeft - imgHeight;
					pdf.addPage();
					pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
					heightLeft -= pdfHeight;
				}

				pdf.save(filename);
			} catch (error) {
				console.error('Error occurred during download:', error);
			} finally {
				// Hide modal and reset
				this.isLoading = false;
				this.$bvModal.hide('print-dispatch-summary');
				this.onReset();
			}
		},

		getFileName() {
			return this.dispatchNo;
		},
		onReset() {
			this.selDispatch = {};
			this.sourceLocation = {};
			this.destinationLocation = {};
			this.deployedByUser = '';
			this.receivedByUser = '';
			this.selectedCopyCount = 2;

			// Check for loader
			this.isLoading = false;
			this.show = true;
		},

		// UTILS
		getNameDisplay(userObj) {
			return userObj ? userObj.firstName + ' ' + userObj.lastName : 'TAWItech Support';
		},
		truncateRemarks(string) {
			if (string.length > 200) {
				let remarks = string.substring(0, 200) + '...';
				return this.breakRemarks(remarks, 50);
			}
			return this.breakRemarks(string, 50);
		},
		breakRemarks(remarks, length) {
			return remarks.length > length ? remarks.replace(new RegExp(`([^\\s]{${length}})`, 'g'), '$1<br>') : remarks;
		}
	},
	beforeDestroy() {
		EventBus.$off('onPrintDispatch');
	},
};
</script>

<style scoped>
.page-container {
	padding-top: 0.5em;
	padding-left: 1.75em;
	padding-right: 1.75em;
	position: relative;
	max-width: 100wh;
	max-height: 100%;
	aspect-ratio: 13 / 9;
	overflow: visible;
}

.section-header {
	text-transform: uppercase;
	color: #122C91;
	font-size: .7rem;
	font-style: normal;
	font-weight: bold;
	line-height: normal;
}

.remarks-section {
	height: 3.5rem;
}

.remarks-value {
	font-size: .7rem;
	font-style: italic;
	background-color: #BFDFCD;
	display: inline-block;
}

.dispatch-summary-block {
	break-inside: avoid;
}

.divider-container {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: -0.7rem;
	margin-left: -0.5rem;
	margin-right: -0.5rem;
	margin-bottom: 0.3rem;
}

.divider-icon {
	font-size: 0.75rem;
}

.divider-line {
	flex-grow: 1;
	border: 0;
	border-top: 0.1px dashed #1f1f1f;
	margin: 0;
}
</style>
